import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaSearch, FaPlus, FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ─── helpers ────────────────────────────────────────────────
const API = "http://nissi-india.com/api/stnListing";

// Map display labels → SP values
const OUTWARD_TYPE_MAP = {
  "From Request": "RO",
  Direct: "DO",
};

const INWARD_TYPE_MAP = {
  "From Outward": "FO",
  "From I/W (Rejected Qty)": "FI",
  "From Direct Outward": "DO",
};

// Default date range: today and 7 days ago
const today = () => new Date().toISOString().split("T")[0];

const sevenAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split("T")[0];
};

const STNOutINListing = () => {
  const navigate = useNavigate();

  const { isDark, skin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ─────────────────────────────────────────────────────────
  // SESSION INFO
  // ─────────────────────────────────────────────────────────

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const SESSION = useMemo(
    () => ({
      branchid: parseInt(branchId) || 0,
      loginuserid: parseInt(userId) || 0,
    }),
    [branchId, userId]
  );

  // ─────────────────────────────────────────────────────────
  // UI TOGGLE STATES
  // ─────────────────────────────────────────────────────────

  const [showOutwardFilters, setShowOutwardFilters] =
    useState(true);

  const [showInwardFilters, setShowInwardFilters] =
    useState(true);

  // ─────────────────────────────────────────────────────────
  // BRANCH DROPDOWN
  // ─────────────────────────────────────────────────────────

  const [branches, setBranches] = useState([]);

  // ─────────────────────────────────────────────────────────
  // OUTWARD FILTER STATE
  // ─────────────────────────────────────────────────────────

  const [outward, setOutward] = useState({
    branch: "",
    dateFrom: sevenAgo(),
    dateTo: today(),
    type: "From Request",
    outwardNo: "",
  });

  // ─────────────────────────────────────────────────────────
  // INWARD FILTER STATE
  // ─────────────────────────────────────────────────────────

  const [inward, setInward] = useState({
    branch: "",
    dateFrom: sevenAgo(),
    dateTo: today(),
    type: "From Outward",
    inwardNo: "",
  });

  // ─────────────────────────────────────────────────────────
  // AUTOCOMPLETE
  // ─────────────────────────────────────────────────────────

  const [outwardSuggestions, setOutwardSuggestions] =
    useState([]);

  const [inwardSuggestions, setInwardSuggestions] =
    useState([]);

  const [showOutwardAC, setShowOutwardAC] =
    useState(false);

  const [showInwardAC, setShowInwardAC] =
    useState(false);

  // ─────────────────────────────────────────────────────────
  // RESULTS
  // ─────────────────────────────────────────────────────────

  const [outwardResults, setOutwardResults] =
    useState([]);

  const [inwardResults, setInwardResults] =
    useState([]);

  // ─────────────────────────────────────────────────────────
  // LOADING
  // ─────────────────────────────────────────────────────────

  const [loadingOut, setLoadingOut] =
    useState(false);

  const [loadingIn, setLoadingIn] =
    useState(false);

  // ─────────────────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────────────────

  const PAGE_SIZE = 10;

  const [outwardPage, setOutwardPage] =
    useState(1);

  const [inwardPage, setInwardPage] =
    useState(1);

  // ═════════════════════════════════════════════════════════
  // ON MOUNT
  // ═════════════════════════════════════════════════════════

  useEffect(() => {
    fetchBranches();
    fetchOutward();
    fetchInward();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═════════════════════════════════════════════════════════
  // FETCH BRANCHES
  // ═════════════════════════════════════════════════════════

  const fetchBranches = async () => {
    try {
      const res = await axios.get(
        `${API}/stnListing_branches`,
        {
          params: {
            branchid: SESSION.branchid,
          },
          headers: commonHeaders,
        }
      );

      if (res.data.success) {
        setBranches(res.data.data || []);
      }
    } catch (error) {
      console.error(error);

      toast.error(
        "Failed to load branch list"
      );
    }
  };

  // ═════════════════════════════════════════════════════════
  // OUTWARD SEARCH
  // ═════════════════════════════════════════════════════════

  const fetchOutward = async (overrides = {}) => {
    const filters = {
      ...outward,
      ...overrides,
    };

    if (!filters.dateFrom || !filters.dateTo) {
      toast.warning(
        "Please select From date and To date"
      );

      return;
    }

    if (filters.dateFrom > filters.dateTo) {
      toast.warning(
        "From date must be less than or equal to To date"
      );

      return;
    }

    setLoadingOut(true);
    setOutwardPage(1);

    try {
      const res = await axios.get(
        `${API}/stnListing_bindDataFixed`,
        {
          params: {
            flag: 1,
            branchid: SESSION.branchid,
            dtfrom: filters.dateFrom,
            dtto: filters.dateTo,
            sbranch: filters.branch || "%",
            no: filters.outwardNo || "%",
            type:
              OUTWARD_TYPE_MAP[filters.type] ||
              "RO",
          },
          headers: commonHeaders,
        }
      );

      setOutwardResults(
        res.data.data || []
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Failed to fetch outward records"
      );
    } finally {
      setLoadingOut(false);
    }
  };

  // ═════════════════════════════════════════════════════════
  // INWARD SEARCH
  // ═════════════════════════════════════════════════════════

  const fetchInward = async (overrides = {}) => {
    const filters = {
      ...inward,
      ...overrides,
    };

    if (!filters.dateFrom || !filters.dateTo) {
      toast.warning(
        "Please select From date and To date"
      );

      return;
    }

    if (filters.dateFrom > filters.dateTo) {
      toast.warning(
        "From date must be less than or equal to To date"
      );

      return;
    }

    setLoadingIn(true);
    setInwardPage(1);

    try {
      const res = await axios.get(
        `${API}/stnListing_bindDataFixed`,
        {
          params: {
            flag: 2,
            branchid: SESSION.branchid,
            dtfrom: filters.dateFrom,
            dtto: filters.dateTo,
            sbranch: filters.branch || "%",
            no: filters.inwardNo || "%",
            type:
              INWARD_TYPE_MAP[filters.type] ||
              "FO",
          },
          headers: commonHeaders,
        }
      );

      setInwardResults(
        res.data.data || []
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Failed to fetch inward records"
      );
    } finally {
      setLoadingIn(false);
    }
  };

  // ═════════════════════════════════════════════════════════
  // AUTOCOMPLETE
  // ═════════════════════════════════════════════════════════

  const fetchSuggestions = useCallback(
    async (
      flag,
      prefixText,
      filters,
      setter,
      setShow
    ) => {
      if (
        !prefixText ||
        prefixText.length < 1
      ) {
        setter([]);
        setShow(false);
        return;
      }

      try {
        const typeVal =
          flag === 1
            ? OUTWARD_TYPE_MAP[
                filters.type
              ] || "RO"
            : INWARD_TYPE_MAP[
                filters.type
              ] || "FO";

        const res = await axios.get(
          `${API}/stnListing_autocomplete`,
          {
            params: {
              flag,
              branchid:
                SESSION.branchid,
              dtfrom:
                filters.dateFrom ||
                sevenAgo(),
              dtto:
                filters.dateTo ||
                today(),
              sbranch:
                filters.branch || "%",
              type: typeVal,
              prefixText,
            },
            headers: commonHeaders,
          }
        );

        setter(
          res.data.data || []
        );

        setShow(true);
      } catch (error) {
        console.error(error);

        setter([]);
      }
    },
    [
      SESSION.branchid,
      commonHeaders,
    ]
  );

  const outwardACTimer =
    useRef(null);

  const inwardACTimer =
    useRef(null);

  const handleOutwardNoChange = (
    val
  ) => {
    setOutward((prev) => ({
      ...prev,
      outwardNo: val,
    }));

    clearTimeout(
      outwardACTimer.current
    );

    outwardACTimer.current =
      setTimeout(() => {
        fetchSuggestions(
          1,
          val,
          {
            ...outward,
            outwardNo: val,
          },
          setOutwardSuggestions,
          setShowOutwardAC
        );
      }, 300);
  };

  const handleInwardNoChange = (
    val
  ) => {
    setInward((prev) => ({
      ...prev,
      inwardNo: val,
    }));

    clearTimeout(
      inwardACTimer.current
    );

    inwardACTimer.current =
      setTimeout(() => {
        fetchSuggestions(
          2,
          val,
          {
            ...inward,
            inwardNo: val,
          },
          setInwardSuggestions,
          setShowInwardAC
        );
      }, 300);
  };

  // ═════════════════════════════════════════════════════════
  // ROW NAVIGATION
  // ═════════════════════════════════════════════════════════

  const handleOutwardRowClick = (
    item
  ) => {
    sessionStorage.setItem(
      "stnoutward",
      item.fld_STN_OutwardNo || ""
    );

    sessionStorage.setItem(
      "Sessionwordwardid",
      item.fld_STN_Outwardid || ""
    );

    const type =
      OUTWARD_TYPE_MAP[
        outward.type
      ] || "RO";

    navigate(
      type === "DO"
        ? "/inventory/STNOutwardDirect"
        : "/inventory/STNOutward"
    );
  };

  const handleInwardRowClick = (
    item
  ) => {
    sessionStorage.setItem(
      "stninward",
      item.fld_STN_InwardNo || ""
    );

    sessionStorage.setItem(
      "stninwardid",
      item.fld_STN_Inwardid || ""
    );

    sessionStorage.setItem(
      "stnoutwardid",
      item.fld_STN_Outwardid || ""
    );

    navigate(
      "/inventory/STNInward",
      {
        state: {
          editMode: true,
        },
      }
    );
  };

  // ═════════════════════════════════════════════════════════
  // CLEAR FILTERS
  // ═════════════════════════════════════════════════════════

  const clearOutwardFilters = () => {
    setOutward({
      branch: "",
      dateFrom: sevenAgo(),
      dateTo: today(),
      type: "From Request",
      outwardNo: "",
    });

    setOutwardResults([]);
    setOutwardSuggestions([]);
    setShowOutwardAC(false);
    setOutwardPage(1);
  };

  const clearInwardFilters = () => {
    setInward({
      branch: "",
      dateFrom: sevenAgo(),
      dateTo: today(),
      type: "From Outward",
      inwardNo: "",
    });

    setInwardResults([]);
    setInwardSuggestions([]);
    setShowInwardAC(false);
    setInwardPage(1);
  };

  // ═════════════════════════════════════════════════════════
  // PAGINATION
  // ═════════════════════════════════════════════════════════

  const paginate = (
    data,
    page
  ) =>
    data.slice(
      (page - 1) * PAGE_SIZE,
      page * PAGE_SIZE
    );

  const totalPages = (data) =>
    Math.max(
      1,
      Math.ceil(
        data.length / PAGE_SIZE
      )
    );

  // ═════════════════════════════════════════════════════════
  // STYLES
  // ═════════════════════════════════════════════════════════

  const inputCls = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelCls = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  const cardCls = `
    rounded-2xl
    shadow-xl
    border
    overflow-hidden
    ${
      isDark
        ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
        : `bg-white ${currentSkin.glass} border-gray-200`
    }
  `;

  // ═════════════════════════════════════════════════════════
  // PAGINATION BAR
  // ═════════════════════════════════════════════════════════

  const PaginationBar = ({
    page,
    setPage,
    data,
  }) => {
    const total =
      totalPages(data);

    if (total <= 1) {
      return null;
    }

    return (
      <div
        className={`flex flex-wrap justify-between items-center gap-3 px-6 py-4 border-t ${
          isDark
            ? "border-gray-700"
            : "border-gray-200"
        }`}
      >
        <span
          className={`text-sm ${
            isDark
              ? "text-gray-300"
              : "text-gray-600"
          }`}
        >
          Page {page} of {total}
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page === 1}
            onClick={() =>
              setPage(
                (p) => p - 1
              )
            }
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
              isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            Prev
          </button>

          <button
            type="button"
            disabled={
              page === total
            }
            onClick={() =>
              setPage(
                (p) => p + 1
              )
            }
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
              isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════
  // RETURN
  // ═════════════════════════════════════════════════════════

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            STN Listing
          </span>
        }
      />

      <div className="space-y-6 mt-6">

        {/* ================================================= */}
        {/* OUTWARD HEADER CARD */}
        {/* ================================================= */}

        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FaSearch className="w-5 h-5 text-white" />
              </div>

              <div>
                <h1 className="text-2xl font-bold">
                  STN Outward
                </h1>

                <p
                  className={`text-sm ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-600"
                  }`}
                >
                  Search and manage STN outward records
                </p>
              </div>

              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {outwardResults.length} records
              </span>

            </div>

            <div className="flex flex-wrap items-center gap-3">

              <button
                type="button"
                onClick={() =>
                  setShowOutwardFilters(
                    !showOutwardFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${
                  showOutwardFilters
                    ? isDark
                      ? "bg-slate-800"
                      : "bg-slate-100"
                    : ""
                }`}
              >
                <FaFilter />

                {showOutwardFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>

              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                onClick={() => {
                  sessionStorage.removeItem(
                    "stnoutward"
                  );

                  navigate(
                    "/inventory/STNOutward"
                  );
                }}
              >
                <FaPlus />

                Add New
              </button>

            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* OUTWARD FILTER CARD */}
        {/* ================================================= */}

        {showOutwardFilters && (
          <div className={cardCls}>

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-lg font-bold">
                  Outward Search Filters
                </h3>

              </div>
            </div>

            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* Branch */}

                <div>
                  <label className={labelCls}>
                    Branch Code / Name
                  </label>

                  <select
                    value={
                      outward.branch
                    }
                    onChange={(e) =>
                      setOutward({
                        ...outward,
                        branch:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  >
                    <option value="">
                      Select Branch
                    </option>

                    {branches.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* Date From */}

                <div>
                  <label className={labelCls}>
                    Date From
                  </label>

                  <input
                    type="date"
                    value={
                      outward.dateFrom
                    }
                    onChange={(e) =>
                      setOutward({
                        ...outward,
                        dateFrom:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  />
                </div>

                {/* Date To */}

                <div>
                  <label className={labelCls}>
                    Date To
                  </label>

                  <input
                    type="date"
                    value={
                      outward.dateTo
                    }
                    onChange={(e) =>
                      setOutward({
                        ...outward,
                        dateTo:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  />
                </div>

                {/* Type */}

                <div>
                  <label className={labelCls}>
                    Outward Type
                  </label>

                  <select
                    value={
                      outward.type
                    }
                    onChange={(e) =>
                      setOutward({
                        ...outward,
                        type:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  >
                    <option>
                      From Request
                    </option>

                    <option>
                      Direct
                    </option>
                  </select>
                </div>

                {/* Outward Number */}

                <div className="relative">

                  <label className={labelCls}>
                    Outward No
                  </label>

                  <input
                    type="text"
                    value={
                      outward.outwardNo
                    }
                    onChange={(e) =>
                      handleOutwardNoChange(
                        e.target
                          .value
                      )
                    }
                    onBlur={() =>
                      setTimeout(
                        () =>
                          setShowOutwardAC(
                            false
                          ),
                        200
                      )
                    }
                    className={
                      inputCls
                    }
                    placeholder="Enter outward number"
                    autoComplete="off"
                  />

                  {showOutwardAC &&
                    outwardSuggestions.length >
                      0 && (
                      <ul
                        className={`absolute z-20 w-full mt-1 border rounded-lg shadow-lg max-h-40 overflow-y-auto ${
                          isDark
                            ? "bg-slate-800 border-slate-600 text-white"
                            : "bg-white border-slate-300 text-slate-900"
                        }`}
                      >
                        {outwardSuggestions.map(
                          (
                            s,
                            i
                          ) => (
                            <li
                              key={
                                i
                              }
                              className={`px-3 py-2 cursor-pointer text-sm ${
                                isDark
                                  ? "hover:bg-slate-700"
                                  : "hover:bg-slate-100"
                              }`}
                              onMouseDown={() => {
                                setOutward(
                                  (
                                    prev
                                  ) => ({
                                    ...prev,
                                    outwardNo:
                                      s,
                                  })
                                );

                                setShowOutwardAC(
                                  false
                                );
                              }}
                            >
                              {
                                s
                              }
                            </li>
                          )
                        )}
                      </ul>
                    )}
                </div>
              </div>

              {/* Filter Buttons */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                <button
                  type="button"
                  onClick={
                    clearOutwardFilters
                  }
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Clear Filters
                </button>

                <button
                  type="button"
                  onClick={() =>
                    fetchOutward()
                  }
                  disabled={
                    loadingOut
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  <FaSearch />

                  {loadingOut
                    ? "Searching..."
                    : "Search Records"}
                </button>

              </div>
            </div>
          </div>
        )}

        {/* ================================================= */}
        {/* OUTWARD RESULT CARD */}
        {/* ================================================= */}

        <div className={cardCls}>

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Outward List
                </h3>

              </div>

              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-slate-700 text-slate-300"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {outwardResults.length} records
              </span>

            </div>
          </div>

          {outwardResults.length >
          0 ? (
            <>
              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>

                      {[
                        "S.No",
                        "Outward No",
                        "Date",
                        "From Branch",
                        "STN Req",
                        "Status",
                      ].map(
                        (h) => (
                          <th
                            key={
                              h
                            }
                            className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide"
                          >
                            {
                              h
                            }
                          </th>
                        )
                      )}

                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }`}
                  >
                    {paginate(
                      outwardResults,
                      outwardPage
                    ).map(
                      (
                        item,
                        index
                      ) => (
                        <tr
                          key={
                            index
                          }
                          onClick={() =>
                            handleOutwardRowClick(
                              item
                            )
                          }
                          className={`transition cursor-pointer ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >

                          <td className="px-4 py-3 font-medium">
                            {(outwardPage -
                              1) *
                              PAGE_SIZE +
                              index +
                              1}
                          </td>

                          <td className="px-4 py-3">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {
                                item.fld_STN_OutwardNo
                              }
                            </span>

                          </td>

                          <td className="px-4 py-3">
                            {item.fld_STN_Date
                              ? new Date(
                                  item.fld_STN_Date
                                ).toLocaleDateString(
                                  "en-GB"
                                )
                              : ""}
                          </td>

                          <td className="px-4 py-3 font-medium">
                            {
                              item.FromBranch
                            }
                          </td>

                          <td className="px-4 py-3">
                            {
                              item.stn_purrequestno
                            }
                          </td>

                          <td className="px-4 py-3">

                            <span
                              className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                                item.fld_Remarks ===
                                "Waiting for Inward"
                                  ? "bg-orange-100 text-orange-700"
                                  : isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {
                                item.fld_Remarks
                              }
                            </span>

                          </td>

                        </tr>
                      )
                    )}
                  </tbody>

                </table>
              </div>

              <PaginationBar
                page={
                  outwardPage
                }
                setPage={
                  setOutwardPage
                }
                data={
                  outwardResults
                }
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-14">

              <FaSearch className="w-10 h-10 text-gray-400 mb-4" />

              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Outward Records Found
              </h3>

              <p className="text-gray-500 text-center max-w-md text-sm">
                Try adjusting the outward search filters or create a new outward record.
              </p>

            </div>
          )}
        </div>

        {/* ================================================= */}
        {/* INWARD HEADER CARD */}
        {/* ================================================= */}

        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FaSearch className="w-5 h-5 text-white" />
              </div>

              <div>

                <h1 className="text-2xl font-bold">
                  STN Inward
                </h1>

                <p
                  className={`text-sm ${
                    isDark
                      ? "text-gray-300"
                      : "text-gray-600"
                  }`}
                >
                  Search and manage STN inward records
                </p>

              </div>

              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {inwardResults.length} records
              </span>

            </div>

            <div className="flex flex-wrap items-center gap-3">

              <button
                type="button"
                onClick={() =>
                  setShowInwardFilters(
                    !showInwardFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${
                  showInwardFilters
                    ? isDark
                      ? "bg-slate-800"
                      : "bg-slate-100"
                    : ""
                }`}
              >
                <FaFilter />

                {showInwardFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>

              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                onClick={() => {
                  sessionStorage.removeItem(
                    "stninward"
                  );

                  navigate(
                    "/inventory/STNInward"
                  );
                }}
              >
                <FaPlus />

                Add New
              </button>

            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* INWARD FILTER CARD */}
        {/* ================================================= */}

        {showInwardFilters && (
          <div className={cardCls}>

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-lg font-bold">
                  Inward Search Filters
                </h3>

              </div>
            </div>

            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* Branch */}

                <div>

                  <label className={labelCls}>
                    Branch Code / Name
                  </label>

                  <select
                    value={
                      inward.branch
                    }
                    onChange={(e) =>
                      setInward({
                        ...inward,
                        branch:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  >
                    <option value="">
                      Select Branch
                    </option>

                    {branches.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}

                  </select>
                </div>

                {/* Date From */}

                <div>

                  <label className={labelCls}>
                    Date From
                  </label>

                  <input
                    type="date"
                    value={
                      inward.dateFrom
                    }
                    onChange={(e) =>
                      setInward({
                        ...inward,
                        dateFrom:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  />

                </div>

                {/* Date To */}

                <div>

                  <label className={labelCls}>
                    Date To
                  </label>

                  <input
                    type="date"
                    value={
                      inward.dateTo
                    }
                    onChange={(e) =>
                      setInward({
                        ...inward,
                        dateTo:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  />

                </div>

                {/* Inward Type */}

                <div>

                  <label className={labelCls}>
                    Inward Type
                  </label>

                  <select
                    value={
                      inward.type
                    }
                    onChange={(e) =>
                      setInward({
                        ...inward,
                        type:
                          e.target
                            .value,
                      })
                    }
                    className={
                      inputCls
                    }
                  >

                    <option>
                      From Outward
                    </option>

                    <option>
                      From I/W (Rejected Qty)
                    </option>

                    <option>
                      From Direct Outward
                    </option>

                  </select>

                </div>

                {/* Inward Number */}

                <div className="relative">

                  <label className={labelCls}>
                    Inward No
                  </label>

                  <input
                    type="text"
                    value={
                      inward.inwardNo
                    }
                    onChange={(e) =>
                      handleInwardNoChange(
                        e.target
                          .value
                      )
                    }
                    onBlur={() =>
                      setTimeout(
                        () =>
                          setShowInwardAC(
                            false
                          ),
                        200
                      )
                    }
                    className={
                      inputCls
                    }
                    placeholder="Enter inward number"
                    autoComplete="off"
                  />

                  {showInwardAC &&
                    inwardSuggestions.length >
                      0 && (
                      <ul
                        className={`absolute z-20 w-full mt-1 border rounded-lg shadow-lg max-h-40 overflow-y-auto ${
                          isDark
                            ? "bg-slate-800 border-slate-600 text-white"
                            : "bg-white border-slate-300 text-slate-900"
                        }`}
                      >

                        {inwardSuggestions.map(
                          (
                            s,
                            i
                          ) => (
                            <li
                              key={
                                i
                              }
                              className={`px-3 py-2 cursor-pointer text-sm ${
                                isDark
                                  ? "hover:bg-slate-700"
                                  : "hover:bg-slate-100"
                              }`}
                              onMouseDown={() => {
                                setInward(
                                  (
                                    prev
                                  ) => ({
                                    ...prev,
                                    inwardNo:
                                      s,
                                  })
                                );

                                setShowInwardAC(
                                  false
                                );
                              }}
                            >
                              {
                                s
                              }
                            </li>
                          )
                        )}

                      </ul>
                    )}

                </div>
              </div>

              {/* Buttons */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                <button
                  type="button"
                  onClick={
                    clearInwardFilters
                  }
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Clear Filters
                </button>

                <button
                  type="button"
                  onClick={() =>
                    fetchInward()
                  }
                  disabled={
                    loadingIn
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  <FaSearch />

                  {loadingIn
                    ? "Searching..."
                    : "Search Records"}
                </button>

              </div>
            </div>
          </div>
        )}

        {/* ================================================= */}
        {/* INWARD RESULTS */}
        {/* ================================================= */}

        <div className={cardCls}>

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Inward List
                </h3>

              </div>

              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-slate-700 text-slate-300"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {inwardResults.length} records
              </span>

            </div>
          </div>

          {inwardResults.length >
          0 ? (
            <>
              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >

                    <tr>

                      {[
                        "S.No",
                        "Inward No",
                        "Date",
                        "From Branch",
                        "STN Outward",
                      ].map(
                        (h) => (
                          <th
                            key={
                              h
                            }
                            className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide"
                          >
                            {
                              h
                            }
                          </th>
                        )
                      )}

                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }`}
                  >

                    {paginate(
                      inwardResults,
                      inwardPage
                    ).map(
                      (
                        item,
                        index
                      ) => (
                        <tr
                          key={
                            index
                          }
                          onClick={() =>
                            handleInwardRowClick(
                              item
                            )
                          }
                          className={`transition cursor-pointer ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >

                          <td className="px-4 py-3 font-medium">
                            {(inwardPage -
                              1) *
                              PAGE_SIZE +
                              index +
                              1}
                          </td>

                          <td className="px-4 py-3">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {
                                item.fld_STN_InwardNo
                              }
                            </span>

                          </td>

                          <td className="px-4 py-3">

                            {item.fld_STN_Date
                              ? new Date(
                                  item.fld_STN_Date
                                ).toLocaleDateString(
                                  "en-GB"
                                )
                              : ""}

                          </td>

                          <td className="px-4 py-3 font-medium">
                            {
                              item.FromBranch
                            }
                          </td>

                          <td className="px-4 py-3">
                            {
                              item.fld_STN_OutwardNo
                            }
                          </td>

                        </tr>
                      )
                    )}

                  </tbody>
                </table>
              </div>

              <PaginationBar
                page={
                  inwardPage
                }
                setPage={
                  setInwardPage
                }
                data={
                  inwardResults
                }
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-14">

              <FaSearch className="w-10 h-10 text-gray-400 mb-4" />

              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Inward Records Found
              </h3>

              <p className="text-gray-500 text-center max-w-md text-sm">
                Try adjusting the inward search filters or create a new inward record.
              </p>

            </div>
          )}
        </div>
      </div>

      {/* ================================================= */}
      {/* TOAST */}
      {/* ================================================= */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />

    </LayoutWrapper>
  );
};

export default STNOutINListing;